// ConnectShopify is the onboarding screen shown when no Shopify
// integration is detected. Phase 3 (deferred-binding install flow)
// wires the real /install/shopify/app-store handshake; for now
// this page describes the three-step setup and links to the
// Shopify App Store listing.

import { ShoppingBag } from 'lucide-react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@avagency/frontend/components/ui/card';
import { Button } from '@avagency/frontend/components/ui/button';

const STEPS = [
  {
    n: 1,
    title: 'Install PO Pilot',
    body: 'from the Shopify App Store. This authorises PO Pilot to read your product catalog and inventory levels.',
  },
  {
    n: 2,
    title: 'Import your SKUs',
    body: 'PO Pilot will detect your existing products and variants automatically. You can also import reorder points from a Stocky CSV export.',
  },
  {
    n: 3,
    title: 'Add your suppliers',
    body: 'and configure reorder thresholds. PO Pilot will alert you when stock drops below target and can draft purchase orders automatically.',
  },
];

export function ConnectShopify() {
  return (
    <div className="mx-auto max-w-2xl space-y-6">
      {/* Hero empty-state illustration */}
      <div className="flex flex-col items-center gap-4 pt-6 pb-2 text-center">
        <div className="po-empty-hero">
          <POMockWidget />
        </div>
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold tracking-tight">
            Connect your Shopify store
          </h1>
          <p className="text-sm text-muted-foreground max-w-sm">
            Watch inventory, draft purchase orders automatically, and never run
            out of stock again.
          </p>
          <div className="flex justify-center pt-1">
            <span className="po-live-chip">auto-reorder ready</span>
          </div>
        </div>
      </div>

      <Card className="po-card-glow">
        <CardHeader>
          <CardTitle className="text-base">Get started in 3 steps</CardTitle>
          <CardDescription>
            Takes about 5 minutes. No credit card required for the trial.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <ol className="space-y-4">
            {STEPS.map((s) => (
              <li key={s.n} className="flex items-start gap-4">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full po-step-num text-sm font-semibold">
                  {s.n}
                </div>
                <div className="space-y-0.5 pt-0.5">
                  <p className="text-sm font-semibold leading-snug">{s.title}</p>
                  <p className="text-sm text-muted-foreground leading-relaxed">{s.body}</p>
                </div>
              </li>
            ))}
          </ol>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <Button
              asChild
              size="lg"
              className="po-btn-install w-full sm:w-auto"
              aria-label="Install PO Pilot on the Shopify App Store"
            >
              <a href="/install/shopify/app-store">
                <ShoppingBag className="mr-2 h-4 w-4" />
                Install on Shopify
              </a>
            </Button>

            <p className="text-xs text-muted-foreground sm:ml-2">
              Already installed?{' '}
              <a href="/api/v1/healthz" className="text-primary hover:underline font-medium">
                Check connection status
              </a>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Decorative mini PO widget — mirrors the LP hero widget aesthetics.
// Pure CSS/SVG, no external deps, aria-hidden.
function POMockWidget() {
  return (
    <div
      aria-hidden
      className="po-mock-widget"
      style={{
        border: '1px solid hsl(153 39% 49% / .2)',
        borderRadius: 16,
        background: 'linear-gradient(180deg, hsl(223 51% 11% / .95), hsl(222 67% 5% / .98))',
        boxShadow: '0 0 0 1px hsl(153 39% 49% / .06), 0 24px 64px hsl(0 0% 0% / .5), inset 0 1px 0 hsl(153 39% 49% / .08)',
        padding: '20px 24px',
        width: 'min(100%, 340px)',
        userSelect: 'none',
      }}
    >
      {/* Widget header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'hsl(220 18% 92%)', marginBottom: 3 }}>
            Draft PO · #PO-2847
          </div>
          <div style={{ fontFamily: 'ui-monospace, monospace', fontSize: 9, letterSpacing: '.16em', textTransform: 'uppercase', color: 'hsl(218 14% 56%)' }}>
            Acme Wholesale
          </div>
        </div>
        <span className="po-live-chip" style={{ fontSize: 9 }}>auto-draft</span>
      </div>

      {/* Mini line items */}
      {[
        { sku: 'SKU-0042', qty: 120, status: 'low' },
        { sku: 'SKU-0107', qty: 48, status: 'oos' },
        { sku: 'SKU-0251', qty: 200, status: 'ok' },
      ].map((item) => (
        <div
          key={item.sku}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '7px 0',
            borderBottom: '1px solid hsl(220 18% 85% / .08)',
            fontSize: 12,
          }}
        >
          <span style={{ fontFamily: 'ui-monospace, monospace', color: 'hsl(218 14% 60%)' }}>
            {item.sku}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: 'hsl(220 18% 85%)', fontWeight: 500 }}>
              {item.qty} units
            </span>
            <span
              style={{
                fontSize: 9,
                letterSpacing: '.12em',
                textTransform: 'uppercase',
                padding: '2px 7px',
                borderRadius: 999,
                background: item.status === 'oos'
                  ? 'hsl(0 100% 78% / .1)'
                  : item.status === 'low'
                    ? 'hsl(41 73% 62% / .1)'
                    : 'hsl(153 39% 49% / .1)',
                color: item.status === 'oos'
                  ? 'hsl(0 100% 75%)'
                  : item.status === 'low'
                    ? 'hsl(41 73% 65%)'
                    : 'hsl(153 39% 58%)',
                border: '1px solid',
                borderColor: item.status === 'oos'
                  ? 'hsl(0 100% 78% / .2)'
                  : item.status === 'low'
                    ? 'hsl(41 73% 62% / .2)'
                    : 'hsl(153 39% 49% / .2)',
              }}
            >
              {item.status}
            </span>
          </div>
        </div>
      ))}

      {/* Footer total */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12, fontSize: 13 }}>
        <span style={{ color: 'hsl(218 14% 56%)' }}>Est. total</span>
        <span style={{ fontWeight: 700, color: 'hsl(153 39% 58%)' }}>$4,320.00</span>
      </div>
    </div>
  );
}
